import { formatEvaluation, type EvaluationScore } from "@chessla/shared";

export type Evaluation = EvaluationScore;

export const hasUsableEvaluation = (evaluation: Evaluation | null): evaluation is Evaluation =>
  evaluation?.mate != null ||
  (evaluation?.cp != null && Number.isFinite(evaluation.cp) && Math.abs(evaluation.cp) !== 10_000);

export const evaluationBarWhiteEdge = (
  layout: "vertical-left" | "horizontal-bottom",
  orientation: "white" | "black",
) => (layout === "horizontal-bottom" ? "left" : orientation === "white" ? "bottom" : "top");

export const normalizeEvaluation = (evaluation: Evaluation | null, sideToMove: "w" | "b"): Evaluation | null => {
  if (!evaluation || sideToMove === "w") return evaluation;
  return {
    cp: evaluation.cp == null ? null : -evaluation.cp,
    mate: evaluation.mate == null ? null : -evaluation.mate,
  };
};

export const displayedEvaluation = (
  historical: Evaluation | null,
  temporary: Evaluation | null,
  temporaryActive: boolean,
) => (temporaryActive ? temporary : historical);

type PositionedEvaluation = { fen: string; evaluation: Evaluation };
export const exactTemporaryEvaluation = (
  displayFen: string,
  live: PositionedEvaluation | null,
  grade: PositionedEvaluation | null,
): Evaluation | null =>
  live?.fen === displayFen ? live.evaluation : grade?.fen === displayFen ? grade.evaluation : null;

export const evaluationPresentation = (evaluation: Evaluation | null) => {
  if (!hasUsableEvaluation(evaluation)) return { label: "Unknown", percent: 50 };
  if (evaluation.mate != null) {
    const score = Math.sign(evaluation.mate) * 10_000;
    return { label: formatEvaluation(evaluation), percent: percentage(score) };
  }
  const cp = evaluation.cp!;
  return { label: formatEvaluation(evaluation), percent: percentage(cp) };
};

const percentage = (cp: number) => 50 + 50 * (2 / (1 + Math.exp(-cp / 300)) - 1);

const START_POSITION = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq -";
/** Before the first move the game is level by definition; the bar shows 0.0 rather than an engine's small edge. */
export const startPositionAware = (fen: string | undefined, evaluation: Evaluation | null): Evaluation | null =>
  fen?.split(" ").slice(0, 4).join(" ") === START_POSITION ? { cp: 0, mate: null } : evaluation;
